import React, { useEffect, useId, useRef } from "react";
import { IconClose } from "./icons";

/**
 * Dialog: on a phone it slides up from the bottom as a full-width panel, centered on larger screens.
 * Escape closes it, focus goes to the close button, the backdrop is clickable.
 */
export default function Modal({ open, onClose, title, children }) {
  const titleId = useId();
  const closeRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  // Focus only on open (onClose changes on every parent render, so it must not pull focus).
  useEffect(() => {
    if (open) closeRef.current?.focus();
  }, [open]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-modal flex items-end justify-center sm:items-center sm:p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
    >
      <div className="absolute inset-0 animate-fade-in bg-slate-950/60" onClick={onClose} aria-hidden="true" />
      <div
        className="relative flex max-h-[92dvh] w-full max-w-3xl animate-pop-in flex-col overflow-hidden rounded-t-2xl border border-slate-200 bg-surface
          shadow-pop dark:border-slate-800 dark:bg-slate-900 dark:shadow-pop-dark sm:max-h-[85dvh] sm:rounded-2xl"
      >
        <div className="flex items-center justify-between gap-3 border-b border-slate-200 px-4 py-3 dark:border-slate-800">
          <h2 id={titleId} className="text-base font-semibold text-slate-900 dark:text-slate-50">
            {title}
          </h2>
          <button
            ref={closeRef}
            type="button"
            onClick={onClose}
            aria-label="Zatvori"
            className="inline-flex h-8 w-8 items-center justify-center rounded-md text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-900 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-slate-50"
          >
            <IconClose className="text-lg" aria-hidden="true" />
          </button>
        </div>
        <div className="scroll-thin overflow-y-auto p-4">{children}</div>
      </div>
    </div>
  );
}
